import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import axios from 'axios';
import addDays from 'date-fns/add_days';

import {
  Link,
  Button,
  Switch,
  Status,
  DatePicker,
  Preloader,
  I18n,
} from 'components';
import { formatDate, formatDateString } from 'utils/formatString';
import { priceProcessing } from 'utils/formatPrice';

import declint from 'utils/declint';
import Channels from '../Channels';
import styles from './styles.pcss';

const cx = classNames.bind(styles);

export default class TvService extends Component {
  constructor(props) {
    super(props);

    this.state = {
      channels: [],
      disconnectionParams: null,
      requestId: null,
      disconnectionRequestDate: new Date(),
      isChannelsListVisible: false,
      isServiceDescriptionVisible: false,
      isDisconnectDialogVisible: false,
      isDisconnecting: false,
      error: null,
    };
  }

  handleDateChange = (value) => {
    this.setState({ disconnectionRequestDate: value });
  }

  fetchChannels = async () => {
    if (this.state.channels.length) return;
    const { packetId, serviceId } = this.props;

    try {
      const { data } = await axios.get('/api/profile/convergence/iptvchannels/', {
        params: { packetId: packetId || serviceId },
      });

      if (data && data.length) {
        data.sort((a, b) => a.tunerPosition - b.tunerPosition);
        this.setState({ channels: data });
      } else {
        this.setState({
          error: {
            message: 'Ошибка при загрузке каналов. Попробуйте ещё раз',
          },
          isChannelsListVisible: false,
        });
      }
    } catch (error) {
      this.setState({ error, isChannelsListVisible: false });
    }
  }

  fetchDisconnectionParams = async () => {
    if (this.state.disconnectionParams) return;
    const {
      preset,
      serviceId,
      splId,
      packetId,
    } = this.props;

    this.setState({ error: null });

    try {
      const { data } = await axios.get('/api/profile/convergence/disableservice/', {
        params: {
          presetServiceId: preset.serviceId,
          splId,
          serviceId,
          packetId,
        },
      });
      if (data.isSuccess) {
        this.setState({ disconnectionParams: data });
      } else {
        this.setState({
          error: {
            message: 'При выполнении операции произошла ошибка. Попробуйте еще раз',
            code: data.errorCode,
          },
          isDisconnectDialogVisible: false,
        });
      }
    } catch (error) {
      this.setState({ error, isDisconnectDialogVisible: false });
    }
  }

  disconnect = async () => {
    const {
      preset,
      serviceId,
      splId,
      packetId,
    } = this.props;
    const { disconnectionRequestDate } = this.state;

    this.setState({ isDisconnecting: true });

    try {
      const { data } = await axios.post('/api/profile/convergence/disableservice/', {
        presetServiceId: preset.serviceId,
        dateDisconnect: disconnectionRequestDate,
        splId,
        serviceId,
        packetId,
      });
      if (data && data.isSuccess) {
        this.setState({
          requestId: data.requestId,
          isDisconnectDialogVisible: false,
          isDisconnecting: false,
        });
      } else {
        this.setState({
          error: {
            message: 'При выполнении операции произошла ошибка. Попробуйте еще раз',
            code: data.errorCode,
          },
          isDisconnectDialogVisible: false,
          isDisconnecting: false,
        });
      }
    } catch (error) {
      this.setState({
        error,
        isDisconnectDialogVisible: false,
        isDisconnecting: false,
      });
    }
  }

  cancelFutureRequest = async () => {
    const { connectionInfo, disconnectionInfo, changeInfo } = this.props;
    const { requestId } = connectionInfo || disconnectionInfo || changeInfo || {};

    this.setState({ error: null });

    try {
      const { data } = await axios.post('/api/profile/convergence/cancelfuturetask/', {
        requestId,
      });

      if (data.isSuccess) {
        this.setState({ futureRequestId: requestId });
      } else {
        this.setState({
          error: {
            message: 'При выполнении операции произошла ошибка. Попробуйте еще раз',
            code: data.errorCode,
          },
        });
      }
    } catch (error) {
      this.setState({
        error: {
          message: 'При выполнении операции произошла ошибка. Попробуйте еще раз',
        },
      });
    }
  }

  render() {
    const {
      className,
      type,
      name,
      link,
      channelCount,
      price,
      ratePeriod,
      selectorType,
      shortDescription,
      devices,
      connectionInfo,
      disconnectionInfo,
      changeInfo,
      serviceTag,
      serviceShortDescription,
      trialPriceText,
      isRemovable,
      isBlocked,
      isMultiroom,
      isBase,
    } = this.props;
    const {
      channels,
      disconnectionParams,
      requestId,
      futureRequestId,
      disconnectionRequestDate,
      isChannelsListVisible,
      isServiceDescriptionVisible,
      isDisconnectDialogVisible,
      isDisconnecting,
      error,
    } = this.state;

    return (
      <div className={cx('component', className)}>
        <div className={cx('service')}>
          <div className={cx('switch')}>
            <Switch
              checked={
                (
                  !isDisconnectDialogVisible
                  && !isDisconnecting
                  && !disconnectionInfo
                  && !requestId
                )
                || Boolean(changeInfo)
              }
              disabled={Boolean(
                isBlocked
                || !isRemovable
                || isDisconnecting
                || disconnectionInfo
                || requestId
                || ~selectorType.indexOf('ReadOnly'),
              )}
              onChange={() => {
                this.setState({
                  isDisconnectDialogVisible: !isDisconnectDialogVisible,
                });
                this.fetchDisconnectionParams();
              }}
              isLoading={Boolean(
                isDisconnecting
                || disconnectionInfo
                || connectionInfo
                || requestId,
              )}
            />
          </div>

          <div className={cx('main')}>
            {type && (
              <div className={cx('type')}>
                {type}
              </div>
            )}
            <Link href={link}>
              {name}
            </Link>
            {isMultiroom && (
              <div className={cx('disclaimer')}>
                Услугу можно отключить в блоке подключенных устройств
              </div>
            )}
            {!isBase && !isMultiroom && selectorType === 'Enabled_ReadOnly' && (
              <div className={cx('disclaimer')}>
                Этот пакет/услугу нельзя отключить. Пакет/услуга входит в тариф по умолчанию
              </div>
            )}
          </div>

          {channelCount > 0 && (
            <div className={cx('channels')}>
              <Link
                onClick={() => {
                  this.setState({
                    isChannelsListVisible: !isChannelsListVisible,
                  });
                  this.fetchChannels();
                }}
              >
                {declint(channelCount, ['%s канал', '%s канала', '%s каналов'])}
              </Link>
            </div>
          )}
          {serviceTag && (
            <div className={cx('serviceTag')}>
              <Link
                onClick={() => {
                  this.setState({
                    isServiceDescriptionVisible: !isServiceDescriptionVisible,
                  });
                }}
              >
                {serviceTag}
              </Link>
            </div>
          )}

          <div className={cx('price')}>
            {devices && (
              <div className={cx('devices')}>
                {declint(devices, ['За %s устройство', 'За %s устройства', 'За %s устройств'])}
              </div>
            )}
            {typeof price === 'number' && `${priceProcessing(price)} ₽ ${ratePeriod}`}
            {trialPriceText && (
              <div className={cx('trialPriceText')} dangerouslySetInnerHTML={{ __html: trialPriceText }} />
            )}
          </div>
        </div>

        {isChannelsListVisible && (
          <Channels
            list={channels}
            packetDescription={shortDescription}
          />
        )}

        {isServiceDescriptionVisible && (
          <div className={cx('serviceShortDescription')}>
            {serviceShortDescription}
          </div>
        )}

        {requestId && (
          <Status className={[cx('message'), 'success']}>
            <I18n
              path="tv.messages.servicesDisconnect"
              vars={{ id: requestId }}
            />
            {' '}
            <Link
              className="light"
              href="/customers/products/mobile/convergence-profile/#/Messages/Requests"
            >
              «Сообщения»
            </Link>
          </Status>
        )}

        {disconnectionInfo && (
          <Status className={[cx('message'), 'attention']} emoji="statusFail">
            <I18n
              path="tv.messages.disconnectionDate"
              vars={{
                date: formatDate(disconnectionInfo.disconnectionDate),
                time: formatDateString(disconnectionInfo.disconnectionDate, 'HH:mm'),
                request_id: disconnectionInfo.requestId,
              }}
            />
            <br />
            {!isBlocked && (
              <Link onClick={this.cancelFutureRequest}>
                Не отключать
              </Link>
            )}
          </Status>
        )}

        {changeInfo && (
          <Status className={[cx('message'), 'attention']} emoji="statusFail">
            {changeInfo.connectionDate || changeInfo.disconnectionDate ? (
              <I18n
                path="tv.messages.nextService"
                vars={{
                  date: formatDate(changeInfo.connectionDate || changeInfo.disconnectionDate),
                  time: formatDateString(changeInfo.connectionDate || changeInfo.disconnectionDate, 'HH:mm'),
                  next_service: changeInfo.nextServiceName,
                  request_id: changeInfo.requestId,
                }}
              />
            ) : (
              <I18n
                path="tv.messages.nextServiceNoDate"
                vars={{
                  next_service: changeInfo.nextServiceName,
                  request_id: changeInfo.requestId,
                }}
              />
            )}
            <br />
            {!isBlocked && (
              <Link onClick={this.cancelFutureRequest}>
                Не изменять
              </Link>
            )}
          </Status>
        )}

        {connectionInfo && (
          <Status className={[cx('message'), 'attention']} emoji="statusFail">
            <I18n
              path="tv.messages.connectionDate"
              vars={{
                date: formatDate(connectionInfo.connectionDate),
                time: formatDateString(connectionInfo.connectionDate, 'HH:mm'),
                request_id: connectionInfo.requestId,
              }}
            />
            <br />
            {!isBlocked && (
              <Link onClick={this.cancelFutureRequest}>
                Не подключать
              </Link>
            )}
          </Status>
        )}

        {futureRequestId && (
          <Status className={[cx('message'), 'success']}>
            <I18n
              path="tv.messages.futureRequest"
              vars={{ id: futureRequestId }}
            />
          </Status>
        )}

        {isDisconnectDialogVisible && (
          <Status className={cx('disconnectionForm')}>
            {disconnectionParams ? (
              <>
                {
                  disconnectionParams.futureOperationEnabled
                  && disconnectionParams.shiftDays > 0 && (
                    <>
                      <div className={cx('disconnectionFormTitle')}>
                        Дата отключения
                      </div>
                      <div className={cx('disconnectionFormDatePicker')}>
                        <DatePicker
                          value={disconnectionRequestDate}
                          min={new Date()}
                          max={addDays(new Date(), disconnectionParams.shiftDays)}
                          onChange={this.handleDateChange}
                        />
                      </div>
                    </>
                  )
                }
                <Button
                  className="default"
                  onClick={this.disconnect}
                  disabled={disconnectionParams.futureOperationEnabled && !disconnectionRequestDate}
                  isLoading={isDisconnecting}
                >
                  Отключить
                </Button>
                <Link
                  className={cx('disconnectionLink')}
                  onClick={() => this.setState({
                    isDisconnectDialogVisible: !isDisconnectDialogVisible,
                  })}
                >
                  Отменить
                </Link>
              </>
            ) : (
              <Preloader
                className={cx('preloader')}
                size="48"
              />
            )}
          </Status>
        )}

        {error && (
          <Status className={[cx('error'), 'error']} emoji="statusFail">
            {error.code === 'FTTB_ACTIVE_REQUEST_EXISTS' ? (
              'Мы еще обрабатываем ваш предыдущий запрос. Пожалуйста, дождитесь его исполнения и повторите попытку'
            ) : error.message}
          </Status>
        )}
      </div>
    );
  }
}

TvService.defaultProps = {
  ratePeriod: 'в месяц',
  isRemovable: true,
};

TvService.propTypes = {
  className: PropTypes.any,
  packetId: PropTypes.string,
  type: PropTypes.string,
  name: PropTypes.string,
  link: PropTypes.string,
  channelCount: PropTypes.number,
  price: PropTypes.number,
  ratePeriod: PropTypes.string,
  selectorType: PropTypes.string,
  shortDescription: PropTypes.string,
  preset: PropTypes.object,
  connectionInfo: PropTypes.object,
  disconnectionInfo: PropTypes.object,
  changeInfo: PropTypes.object,
  serviceId: PropTypes.string,
  splId: PropTypes.number,
  devices: PropTypes.number,
  isRemovable: PropTypes.bool,
  isBlocked: PropTypes.bool,
  isMultiroom: PropTypes.bool,
  isBase: PropTypes.bool,
  serviceTag: PropTypes.string,
  serviceShortDescription: PropTypes.string,
  trialPriceText: PropTypes.string,
};
